import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useLocation, useNavigationType } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import Footer from '../../component/Footer';
import Header from '../../component/Header';
import Productfilter from '../../component/Productfilter';
import Productmobilefilter from '../../component/Productmobilefilter';
import { addToWishlist } from "../../action";
import "./Products.css";
import InfiniteProducts from '../../component/Common/InfiniteProducts';
import { useGetSubcategoryQuery, useGetProductsInfiniteQuery } from '../../store/api/productsApi';

const per_page = 40; // you can change as needed

export default function Products() {
  const { id, catId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const dispatch = useDispatch();
  const wishlistItems = useSelector(state => state?.cart?.wishlistItems);

  // Blloko browser-in nga scroll automatik kur kthehesh Back
  useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // We no longer need local page state, infiniteQuery handles it
  // const [page, setPage] = useState(0);

  // Restore filters from location.state or sessionStorage if available (when navigating back from product details)
  const savedFilters = location.state?.filters || (() => {
    try {
      const saved = sessionStorage.getItem('productsFilters');
      if (saved) {
        const parsedFilters = JSON.parse(saved);
        // Check if filters are older than 30 minutes (1800000 ms)
        const maxAge = 30 * 60 * 1000; // 30 minutes
        const now = Date.now();
        if (parsedFilters.timestamp && (now - parsedFilters.timestamp) > maxAge) {
          // Filters are too old, clear them
          sessionStorage.removeItem('productsFilters');
          return null;
        }
        return parsedFilters;
      }
      return null;
    } catch {
      return null;
    }
  })();
  const hasRestoredFilters = useRef(false);
  const lastLocationKey = useRef(location.key);

  // Filter states - initialize from location.state if available, otherwise empty
  const [categoryCheck, setCategoryCheck] = useState(() => {
    return savedFilters?.categoryCheck || [];
  });
  const [brandCheck, setBrandCheck] = useState(() => {
    return savedFilters?.brandCheck || [];
  });
  const [sizeCheck, setSizeCheck] = useState(() => {
    return savedFilters?.sizeCheck || [];
  });
  const [gtPrice, setGtPrice] = useState(() => {
    return savedFilters?.gtPrice;
  });
  const [ltPrice, setLtPrice] = useState(() => {
    return savedFilters?.ltPrice;
  });
  const [sortPrice, setSortPrice] = useState(() => {
    return savedFilters?.sortPrice || "";
  });
  const discountQuery = searchParams.get('discount');
  const urlPage = Math.max(1, Number(searchParams.get('page') || 1) || 1);
  // Parse discount=yes/valentinesday or discount=no/valentinesday format
  let normalizedDiscount = "";
  let discountNo = null;
  if (discountQuery) {
    if (discountQuery.includes('/')) {
      const parts = discountQuery.split('/');
      if (parts[0] === 'yes' || parts[0] === 'no') {
        normalizedDiscount = parts[0];
        discountNo = parts[1] || null;
      } else {
        normalizedDiscount = discountQuery === 'yes' ? 'yes' : discountQuery === 'no' ? 'no' : "";
      }
    } else {
      normalizedDiscount = discountQuery === 'yes' ? 'yes' : discountQuery === 'no' ? 'no' : "";
    }
  }
  const [offers, setOffers] = useState(() => {
    return savedFilters?.offers !== undefined ? savedFilters.offers : normalizedDiscount;
  });
  const [discountNoFilter, setDiscountNoFilter] = useState(() => {
    return savedFilters?.discountNo !== undefined ? savedFilters.discountNo : discountNo;
  });
  
  // Update offers from URL only if we haven't restored filters from state
  useEffect(() => {
    // Skip if we've restored filters from location.state
    if (hasRestoredFilters.current) {
      return;
    }
    
    if (discountQuery === null) {
      setOffers((prev) => (prev ? "" : prev));
      setDiscountNoFilter(null);
      return;
    }
    if (normalizedDiscount) {
      setOffers(normalizedDiscount);
    }
    if (discountNo) {
      setDiscountNoFilter(discountNo);
    } else {
      setDiscountNoFilter(null);
    }
  }, [discountQuery, normalizedDiscount, discountNo]);
  
  // Check if catId is a gender value (female, male, kids, unisex)
  const genderValues = ['female', 'male', 'kids', 'unisex'];
  const isGenderInUrl = catId && genderValues.includes(catId.toLowerCase());
  const [selectedGender, setSelectedGender] = useState(() => {
    return savedFilters?.selectedGender !== undefined 
      ? savedFilters.selectedGender 
      : (isGenderInUrl ? catId.toLowerCase() : undefined);
  });
  
  // Update selectedGender when catId changes and it's a gender (but not if we restored from state)
  useEffect(() => {
    if (!hasRestoredFilters.current && isGenderInUrl) {
      setSelectedGender(catId.toLowerCase());
    }
  }, [catId, isGenderInUrl]);

  // Restore filters when location.state changes (e.g., when navigating back)
  useEffect(() => {
    const currentFilters = location.state?.filters;
    const locationKeyChanged = lastLocationKey.current !== location.key;
    
    // Update location key tracker
    if (locationKeyChanged) {
      lastLocationKey.current = location.key;
    }
    
    // Try to restore from location.state first, then fallback to sessionStorage
    let filtersToRestore = currentFilters;
    
    if (!filtersToRestore) {
      // Fallback to sessionStorage (for browser back button)
      try {
        const savedFiltersStr = sessionStorage.getItem('productsFilters');
        if (savedFiltersStr) {
          const parsedFilters = JSON.parse(savedFiltersStr);
          // Check if filters are older than 30 minutes (1800000 ms)
          const maxAge = 30 * 60 * 1000; // 30 minutes
          const now = Date.now();
          if (parsedFilters.timestamp && (now - parsedFilters.timestamp) > maxAge) {
            // Filters are too old, clear them
            sessionStorage.removeItem('productsFilters');
            filtersToRestore = null;
          } else {
            filtersToRestore = parsedFilters;
          }
        }
      } catch (e) {
        console.warn('Failed to restore filters from sessionStorage:', e);
      }
    }
    
    // Restore filters if we have them and location changed (navigated back)
    if (filtersToRestore && locationKeyChanged) {
      setCategoryCheck(filtersToRestore.categoryCheck || []);
      setBrandCheck(filtersToRestore.brandCheck || []);
      setSizeCheck(filtersToRestore.sizeCheck || []);
      setGtPrice(filtersToRestore.gtPrice);
      setLtPrice(filtersToRestore.ltPrice);
      setSortPrice(filtersToRestore.sortPrice || "");
      // Always restore offers from saved filters if available
      if (filtersToRestore.offers !== undefined) {
        setOffers(filtersToRestore.offers);
      }
      if (filtersToRestore.discountNo !== undefined) {
        setDiscountNoFilter(filtersToRestore.discountNo);
      }
      if (filtersToRestore.selectedGender !== undefined) {
        setSelectedGender(filtersToRestore.selectedGender);
      }
      hasRestoredFilters.current = true;
    } else if (!filtersToRestore && locationKeyChanged) {
      // Reset flag if filters are cleared (e.g., direct navigation)
      hasRestoredFilters.current = false;
      // Clear sessionStorage if no filters
      try {
        sessionStorage.removeItem('productsFilters');
      } catch (e) {
        // Ignore
      }
    }
  }, [location.state, location.key]);

  const displayWidth = window.innerWidth;
  const listContainerRef = useRef(null);
  const didNormalizePageRef = useRef(false);


  // Fetch subcategory via RTK Query (automatically cached)
  const { data: linkedCategories } = useGetSubcategoryQuery(id, { skip: !id });

  // Derived params object for RTK Query - use useMemo to react to categoryCheck and linkedCategories
  const productsQueryParams = useMemo(() => {
    // Flatten categoryCheck (array of arrays) and merge with linkedCategories
    const mergedCategoryIds = [...new Set([
      ...(categoryCheck.flat()),
      ...(linkedCategories || [])
    ])];
    
    return {
      catAndGender: categoryCheck,
      brands: brandCheck,
      sizes: sizeCheck,
      gtPrice,
      ltPrice,
      sort: sortPrice,
      id,
      catId: mergedCategoryIds,
      per_page,
      discount: offers,
      discountNo: discountNoFilter,
      selectedGender: selectedGender
    };
  }, [categoryCheck, linkedCategories, brandCheck, sizeCheck, gtPrice, ltPrice, sortPrice, id, offers, discountNoFilter, selectedGender]);
  const allowCategorylessListing = Boolean(normalizedDiscount);

  const {
    data: productsResponse,
    isLoading, // True for initial load
    isFetching, // True for any fetch (initial or load more)
    isError,
    refetch,
    fetchNextPage,
    hasNextPage
  } = useGetProductsInfiniteQuery(productsQueryParams, {
    skip: !id && !allowCategorylessListing,
    refetchOnMountOrArgChange: false,
    refetchOnReconnect: false,
    refetchOnFocus: false
  });

  const loadedPagesCount = productsResponse?.pages?.length || 0;
  const productCount = productsResponse?.pages?.[0]?.totalCount;

  const products = useMemo(() => {
    const pages = productsResponse?.pages || [];
    const map = new Map();

    pages.forEach(page => {
      const list = Array.isArray(page?.products) ? page.products : (Array.isArray(page) ? page : []);
      list.forEach(p => {
        // skip non-product objects like { msg: "No products found" }
        if (!p || typeof p !== 'object' || p.msg) return;

        const key = p.externalId ?? p._id ?? p.id ?? JSON.stringify(p);
        if (!map.has(key)) map.set(key, p);
      });
    });

    return Array.from(map.values());
  }, [productsResponse]);
  // const totalProducts = productsResponse?.totalCount || 0; // Not strictly needed for "load more" check if we use hasNextPage




  const addItemToWishlist = async (item) => {
    const updatedWishlist = [...(wishlistItems || [])];
    const index = updatedWishlist.findIndex(x => x?._id === item?._id);
    if (index === -1) {
      updatedWishlist.push(item);
    } else {
      updatedWishlist.splice(index, 1);
    }
    dispatch(addToWishlist(updatedWishlist));
  };


  const setCategoryToggleByLength = (e) => {
    setCategoryCheck(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      // Convert single ID to array if needed
      const categoryArray = Array.isArray(e) ? e : [e];
      const categoryLength = categoryArray.length;
      
      // Check if an array with the same length exists
      const existsSameLength = safePrev.some(arr => 
        Array.isArray(arr) && 
        arr.length === categoryLength &&
        arr.every((id, idx) => String(id) === String(categoryArray[idx]))
      );
      
      if (existsSameLength) {
        // Remove the matching array
        const next = safePrev.filter(arr => 
          !(Array.isArray(arr) && 
            arr.length === categoryLength &&
            arr.every((id, idx) => String(id) === String(categoryArray[idx])))
        );
        return next;
      } else {
        // Add the new array
        const next = [...safePrev, categoryArray];
        return next;
      }
    });
  };



  const setBrandPush = (e) => setBrandCheck(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  const setSizePush = (e) => setSizeCheck(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  const setPricePush = (min, max) => {
    setGtPrice(min);
    setLtPrice(max);
  };

  const setSortPush = (e) => setSortPrice(e);
  const setOfferPushData = (e) => setOffers(e);
  const setGenderPushData = (e) => setSelectedGender(e);

  const resetFilter = () => {
    setSortPrice();
    // Preserve discount filter if we're on a discount=yes page,
    // or on a campaign page (discountNo present, even without discount=yes)
    if (normalizedDiscount === 'yes' || discountNo) {
      // Keep the discount filter from URL
      setOffers(normalizedDiscount);
      setDiscountNoFilter(discountNo);
    } else {
      // Clear discount filter for all other cases (no discount param, plain discount=no, etc.)
      setOffers();
      setDiscountNoFilter(null);
    }
    setSelectedGender();
    setLtPrice();
    setGtPrice();
    setBrandCheck([]);
    setSizeCheck([]);
    setCategoryCheck([]);
    
    // Clear sessionStorage to prevent filters from being restored
    try {
      sessionStorage.removeItem('productsFilters');
    } catch (e) {
      console.warn('Failed to clear filters from sessionStorage:', e);
    }
  };

  const handleLoadMore = () => {
    // Update URL first (shareable / back-restorable), then fetch next page.
    // Use replace to avoid polluting history with infinite-scroll events.
    const next = new URLSearchParams(searchParams);
    next.set('page', String(urlPage + 1));
    setSearchParams(next, { replace: true });
    fetchNextPage();
  };
  // Stable filters snapshot (avoid new object each render)
  const filtersForNavigation = useMemo(() => {
    return {
      categoryCheck,
      brandCheck,
      sizeCheck,
      gtPrice,
      ltPrice,
      sortPrice,
      offers,
      discountNo: discountNoFilter,
      selectedGender
    };
  }, [categoryCheck, brandCheck, sizeCheck, gtPrice, ltPrice, sortPrice, offers, discountNoFilter, selectedGender]);

  // Keep filters in sessionStorage only when they change (fallback for browser back)
  useEffect(() => {
    try {
      sessionStorage.setItem('productsFilters', JSON.stringify({ ...filtersForNavigation, timestamp: Date.now() }));
    } catch (e) {
      // Ignore
    }
  }, [filtersForNavigation]);

  const handleProductClick = () => {
    // ProductItem ruan plp_scrollY + plp_productId
  };
  const loadingText = isLoading ? 'Loading products....' : (products.length === 0 && !isFetching ? 'Nuk ka produkte të disponueshme' : '');

  // Ensure we have loaded as many pages as the URL indicates (Option A: incremental fetch)
  useEffect(() => {
    if (!urlPage) return;
    if (isLoading || isFetching) return;
    if (!hasNextPage) return;
    if (loadedPagesCount >= urlPage) return;

    // Fetch until we reach the requested page count.
    fetchNextPage();
  }, [urlPage, loadedPagesCount, hasNextPage, isLoading, isFetching, fetchNextPage]);

  // When filters/query args change in a way that should reset listing, reset URL page to 1.
  const lastArgsKeyRef = useRef('');
  useEffect(() => {
    const argsKey = JSON.stringify(productsQueryParams);
    if (!lastArgsKeyRef.current) {
      lastArgsKeyRef.current = argsKey;
      return;
    }
    if (lastArgsKeyRef.current !== argsKey) {
      lastArgsKeyRef.current = argsKey;
      const next = new URLSearchParams(searchParams);
      next.set('page', '1');
      setSearchParams(next, { replace: true });
      // Clear PLP restore markers for a "new" listing
      try {
        sessionStorage.removeItem('plp_scrollY');
        sessionStorage.removeItem('plp_productId');
      } catch {}
    }
  }, [productsQueryParams, searchParams, setSearchParams]);

  // Scroll restoration (Back / POP)
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (navigationType !== 'POP') return;
    if (didRestoreRef.current) return;
    // Wait until we have enough pages rendered for the requested URL page
    if (loadedPagesCount < urlPage) return;

    const productId = (() => {
      try { return sessionStorage.getItem('plp_productId'); } catch { return null; }
    })();
    const scrollStr = (() => {
      try { return sessionStorage.getItem('plp_scrollY'); } catch { return null; }
    })();

    // Nëse nuk ka asgjë për restore, mos vazhdo
    if (!productId && !scrollStr) return;

    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const initialY = (() => {
      if (!scrollStr) return null;
      const y = Number(scrollStr);
      return Number.isNaN(y) ? null : y;
    })();

    const alignToProduct = (attempt = 0) => {
      if (!productId) {
        didRestoreRef.current = true;
        try {
          sessionStorage.removeItem('plp_scrollY');
          sessionStorage.removeItem('plp_productId');
        } catch {}
        return;
      }

      const el = document.getElementById(`product-${productId}`);
      if (el || attempt >= 10) {
        if (el) {
          const header =
            document.querySelector('header') ??
            document.querySelector('.header') ??
            document.querySelector('[class*="header"]');
          const headerRect = header ? header.getBoundingClientRect() : null;
          const headerHeight = headerRect ? headerRect.height : 0;
          const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 8;
          window.scrollTo({ top, behavior: 'instant' });
        }

        didRestoreRef.current = true;
        try {
          sessionStorage.removeItem('plp_scrollY');
          sessionStorage.removeItem('plp_productId');
        } catch {}
        return;
      }

      window.setTimeout(() => alignToProduct(attempt + 1), 60);
    };

    // Defer to allow virtualization to measure/render
    const t = window.setTimeout(() => {
      // Hapi 1: shko afërsisht te pozicioni i ruajtur
      if (initialY !== null) {
        window.scrollTo(0, initialY);
      }
      // Hapi 2: pasi lista të jetë renderuar, ripozicionohu fiks te produkti
      alignToProduct(0);
    }, 50);

    return () => window.clearTimeout(t);
  }, [navigationType, loadedPagesCount, urlPage]);

  // Nëse hapet direkt një URL me ?page>1 (p.sh. rifreskim),
  // dhe nuk kemi state për t'u kthyer mbrapa nga produkti,
  // reseto page në 1 që lista të sillet si hapje e re.
  useEffect(() => {
    if (didNormalizePageRef.current) return;
    if (navigationType !== 'POP') return;
    if (urlPage <= 1) return;

    let hasScrollState = false;
    let hasProductState = false;
    try {
      hasScrollState = !!sessionStorage.getItem('plp_scrollY');
      hasProductState = !!sessionStorage.getItem('plp_productId');
    } catch {}

    // Nëse s'ka asnjë state për kthim mbrapa, trajtoje si rifreskim/hapje direkte
    if (!hasScrollState && !hasProductState) {
      didNormalizePageRef.current = true;
      const next = new URLSearchParams(searchParams);
      next.set('page', '1');
      setSearchParams(next, { replace: true });
    }
  }, [navigationType, urlPage, searchParams, setSearchParams]);


  return (
    <div>
      <Header />
      <div ref={listContainerRef}>
        <div className="main_wrapper_products">
          {displayWidth < 767 ? (
            <div className="mobile-filter-bar">
            <Productmobilefilter
              setCategoryPushData={setCategoryToggleByLength}
              setBrandPushData={setBrandPush}
              setSizePushData={setSizePush}
              setPricePushData={setPricePush}
              setSortPushData={setSortPush}
              setOfferPush={setOfferPushData}
              setGenderPushData={setGenderPushData}
              filterBtn={() => { }}
              pageType="products"
              sortInBar
              resetFilter={resetFilter}
              activeOffer={offers}
              activeCategories={categoryCheck}
              activeBrands={brandCheck}
              activeSizes={sizeCheck}
              activeGender={selectedGender}
              activeSort={sortPrice}
              activeMinPrice={gtPrice}
              activeMaxPrice={ltPrice}
              productCount={productCount}
              isDiscountPage={normalizedDiscount === "yes"}
            />
            </div>
          ) : (
            <Productfilter
              setCategoryPushData={setCategoryToggleByLength}
              setBrandPushData={setBrandPush}
              setSizePushData={setSizePush}
              setPricePushData={setPricePush}
              setSortPushData={setSortPush}
              setOfferPush={setOfferPushData}
              setGenderPushData={setGenderPushData}
              filterBtn={() => {
                // setPage(0); // Not needed, changing filters triggers new query automatically
              }}
              pageType="products"
              resetFilter={resetFilter}
              activeOffer={offers}
              isDiscountPage={normalizedDiscount === 'yes'}
              activeCategories={categoryCheck}
              activeBrands={brandCheck}
              activeSizes={sizeCheck}
              activeGender={selectedGender}
              activeSort={sortPrice}
              activeMinPrice={gtPrice}
              activeMaxPrice={ltPrice}
              productCount={productCount}
            />
          )}
        </div>

        <div className={`w-full flex items-center justify-center ${products?.length === 0 ? 'no-rec-sec' : ''}`}>
          {isLoading ? (
            <div className="flex justify-center items-center h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-r-transparent">
              <span className="sr-only">Loading...</span>
            </div>
          ) : isError ? (
            <div className="text-center">
              <div>Gabim gjatë ngarkimit të produkteve. Provoni përsëri.</div>
              <button className="mt-2 px-4 py-2 bg-black text-white" onClick={() => refetch()}>Rifresko</button>
            </div>
          ) : (
            products.length === 0 && !isFetching && <div>{loadingText}</div>
          )}
        </div>

        <div className="mx-auto container product-list">
          <InfiniteProducts
            products={products}
            addItemToWishlist={addItemToWishlist}
            onProductClick={handleProductClick}
            onLoadMore={handleLoadMore}
            hasNextPage={hasNextPage}
            isFetching={isFetching}
            filters={filtersForNavigation}
          />
        </div>
      </div>

      {/* Load More button */}
      {hasNextPage && !isFetching && (
        <div className="flex justify-center my-6">

          <button
            className="px-6 py-2 bg-black text-white rounded disabled:opacity-50"
            onClick={handleLoadMore}
            disabled={isFetching}
          >
            Shfaq më shumë produkte
          </button>
        </div>
      )}

      {/* Loading spinner for next pages */}
      {isFetching && !isLoading && (
        <div className="flex justify-center my-4">
          <div className="h-8 w-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      )}

      <Footer />
    </div>
  );
}
